import { loadStripe } from '@stripe/stripe-js'
import { useModal } from '@wrisk/ui-components'
import React, { useCallback } from 'react'

import {
  UnauthorizedModalContent,
  useAdminUnauthorised,
  usePrincipal,
} from '../application/authentication'
import {
  AdminSettleModalContent,
  AdminSettleModalFormProps,
} from '../application/organisms/admin'
import { useProposal } from '../application/product/proposal/ProposalProvider'
import { getPolicyholder } from '../domain'
import { useWriskTranslation } from '../infrastructure/internationalisation'
import { useApiErrorHandlingAsyncCallback } from './auth'
import { useMotoCheckoutRequest, useStripeCheckoutRequest } from './stripeCheckoutRequest'

const {
  stripe: { apiKey, stripeAccount },
} = CONFIG

interface StripeCheckoutAdmin {
  saveCard: boolean
  proposalCode: string
  onSuccess: () => void
}

interface StripeCheckoutUser {
  saveCard: boolean
  proposalCode: string
  successPath: string
  cancelPath: string
}

export const useStripeCheckoutUser = ({
  saveCard,
  proposalCode,
  successPath,
  cancelPath,
}: StripeCheckoutUser) => {
  const { apiClient } = usePrincipal()

  const request = useStripeCheckoutRequest({
    saveCard,
    successPath,
    cancelPath,
  })

  const adminUnauthorised = useAdminUnauthorised()

  return useApiErrorHandlingAsyncCallback(
    adminUnauthorised(async () => {
      const stripe = await loadStripe(apiKey, { stripeAccount })

      await stripe?.redirectToCheckout(
        await apiClient.stripeCheckoutProposal(proposalCode, request),
      )
    }),
  )
}

export const useStripeCheckoutAdmin = ({
  saveCard,
  proposalCode,
  onSuccess,
}: StripeCheckoutAdmin) => {
  const { show } = useModal()
  const { i18n, t } = useWriskTranslation()
  const { apiClient, isAdmin } = usePrincipal()

  const {
    proposal: { quote },
  } = useProposal()

  const {
    data: { dateOfBirth },
  } = getPolicyholder(quote)

  const request = useMotoCheckoutRequest({
    saveCard,
  })

  const invokeApi = useCallback(
    async (props: AdminSettleModalFormProps) =>
      await apiClient.stripeMotoCheckoutProposal(proposalCode, {
        ...request,
        ...props,
      }),
    [apiClient, i18n.language, t],
  )

  return useApiErrorHandlingAsyncCallback(async () => {
    show({
      content: isAdmin ? (
        <AdminSettleModalContent
          invokeApi={invokeApi}
          dateOfBirth={dateOfBirth}
          onSuccess={onSuccess}
        />
      ) : (
        <UnauthorizedModalContent />
      ),
    })
  })
}
